import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { Spinner } from 'react-bootstrap';
import { useDocumentRequirement } from '../contexts/DocumentRequirementContext';

const DocumentRequirementCheck = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isCompleted, setIsCompleted, isChecking, setIsChecking } = useDocumentRequirement();
  
  console.log('DocumentRequirementCheck component MOUNTING - location:', location.pathname, 'isCompleted:', isCompleted, 'isChecking:', isChecking);
  
  const [hasRequirements, setHasRequirements] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    console.log('DocumentRequirementCheck useEffect - hasChecked:', hasChecked, 'isCompleted:', isCompleted, 'isChecking:', isChecking);
    
    // If already checking, skip check
    if (isChecking) {
      console.log('Already checking requirements, skipping');
      return;
    }
    
    // If we've already checked this session, skip
    if (hasChecked) {
      console.log('Already checked this session, skipping');
      return;
    }
    
    // Always run the check to get fresh data from server
    checkDocumentRequirements();
  }, []); // Only run once on mount

  useEffect(() => {
    return () => {
      console.log('DocumentRequirementCheck component UNMOUNTING');
    };
  }, []);

  const checkDocumentRequirements = async () => {
    try {
      setIsChecking(true);
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No token found, skipping document requirement check');
        setIsChecking(false);
        setHasChecked(true);
        return;
      }



      // Skip check for certain routes
      const skipRoutes = [
        '/document-completion',
        '/complete-profile',
        '/login',
        '/signup',
        '/forgot-password',
        '/reset-password',
        '/',
        '/about',
        '/contact',
        '/terms',
        '/privacy'
      ];

      if (skipRoutes.some(route => {
        // More specific matching to avoid /dashboard being matched by /
        if (route === '/') {
          return location.pathname === '/';
        }
        return location.pathname.startsWith(route);
      })) {
        console.log('Skipping document requirement check for route:', location.pathname);
        setIsChecking(false);
        setHasChecked(true);
        return;
      }

      console.log('Checking document requirements for user...');

      // Get user data
      const response = await axios.get(`${API_ENDPOINTS.AUTH}/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const user = response.data;
      console.log('User data:', user);
      console.log('Document requirements:', user.documentRequirements);
      
            // Check if user has already completed document requirements
      console.log('User documentRequirementsCompleted:', user.documentRequirementsCompleted);
      if (user.documentRequirementsCompleted) {
        console.log('User has already completed document requirements, allowing access to dashboard');
        setIsCompleted(true);
        setIsChecking(false);
        setHasChecked(true);
        return;
      }
      
      // Check if user has document requirements that need to be completed
      let documentRequirements = null;
      if (user.documentRequirements) {
        try {
          documentRequirements = typeof user.documentRequirements === 'string' 
            ? JSON.parse(user.documentRequirements) 
            : user.documentRequirements;
        } catch (e) {
          console.error('Error parsing document requirements:', e);
          documentRequirements = null;
        }
      }
      
      const hasDocumentRequirements = documentRequirements &&
          (documentRequirements.packages?.length > 0 ||
            documentRequirements.documents?.length > 0 ||
            documentRequirements.documentRequests?.length > 0 ||
            documentRequirements.forms?.length > 0);
      
      if (hasDocumentRequirements) {
        
        console.log('User has document requirements, checking completion status...');
        
        try {
          // Check if user has completed all requirements
          const [assignmentsRes, submissionsRes] = await Promise.all([
            axios.get(`${API_ENDPOINTS.ONBOARDING}/assignments/me`, {
              headers: { Authorization: `Bearer ${token}` }
            }),
            axios.get(`${API_ENDPOINTS.ONBOARDING}/submissions/me`, {
              headers: { Authorization: `Bearer ${token}` }
            })
          ]);

          const assignments = assignmentsRes.data;
          const submissions = submissionsRes.data;
          
          // Check if responses are HTML (error pages)
          if (typeof assignments === 'string' && assignments.includes('<!doctype html>')) {
            console.error('Assignments API returned HTML instead of JSON');
            throw new Error('Authentication error - please log in again');
          }
          if (typeof submissions === 'string' && submissions.includes('<!doctype html>')) {
            console.error('Submissions API returned HTML instead of JSON');
            throw new Error('Authentication error - please log in again');
          }
          
          console.log('Assignments:', assignments);
          console.log('Submissions:', submissions);

          // Check if all required items are completed
          const requiredPackages = documentRequirements.packages || [];
          const requiredDocuments = documentRequirements.documents || [];
          const requiredDocumentRequests = documentRequirements.documentRequests || [];
          const requiredForms = documentRequirements.forms || [];

          // Ensure submissions is an array
          if (!Array.isArray(submissions)) {
            console.error('Submissions is not an array:', submissions);
            throw new Error('Invalid submissions data received');
          }
          
          // Check for submitted and approved submissions
          const submittedSubmissions = submissions.filter(sub => sub.status === 'submitted');
          const approvedSubmissions = submissions.filter(sub => sub.status === 'approved');
          
          // Count unique assignments that have been submitted/approved
          const submittedPackageIds = new Set(submittedSubmissions
            .filter(sub => sub.assignment?.packageId && requiredPackages.includes(sub.assignment.packageId))
            .map(sub => sub.assignment.packageId)
          );
          
          const submittedDocumentIds = new Set(submittedSubmissions
            .filter(sub => sub.assignment?.documentId && requiredDocuments.includes(sub.assignment.documentId))
            .map(sub => sub.assignment.documentId)
          );
          
          const approvedPackageIds = new Set(approvedSubmissions
            .filter(sub => sub.assignment?.packageId && requiredPackages.includes(sub.assignment.packageId))
            .map(sub => sub.assignment.packageId)
          );
          
          const approvedDocumentIds = new Set(approvedSubmissions
            .filter(sub => sub.assignment?.documentId && requiredDocuments.includes(sub.assignment.documentId))
            .map(sub => sub.assignment.documentId)
          );

          const submittedDocumentRequestIds = new Set(submittedSubmissions
            .filter(sub => sub.assignment?.documentRequestId && requiredDocumentRequests.includes(sub.assignment.documentRequestId))
            .map(sub => sub.assignment.documentRequestId)
          );

          const approvedDocumentRequestIds = new Set(approvedSubmissions
            .filter(sub => sub.assignment?.documentRequestId && requiredDocumentRequests.includes(sub.assignment.documentRequestId))
            .map(sub => sub.assignment.documentRequestId)
          );
          const formSubmissionsRes = await axios.get(`${API_ENDPOINTS.ONBOARDING}/form-submissions/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const formSubmissions = Array.isArray(formSubmissionsRes.data) ? formSubmissionsRes.data : [];
          const submittedFormIds = new Set(formSubmissions
            .filter(sub => sub.status === 'submitted' && requiredForms.includes(sub.assignment?.formId))
            .map(sub => sub.assignment.formId)
          );
          const approvedFormIds = new Set(formSubmissions
            .filter(sub => sub.status === 'approved' && requiredForms.includes(sub.assignment?.formId))
            .map(sub => sub.assignment.formId)
          );

          const totalRequired = requiredPackages.length + requiredDocuments.length + requiredDocumentRequests.length + requiredForms.length;
          const totalSubmitted = submittedPackageIds.size + submittedDocumentIds.size + submittedDocumentRequestIds.size + submittedFormIds.size;
          const totalApproved = approvedPackageIds.size + approvedDocumentIds.size + approvedDocumentRequestIds.size + approvedFormIds.size;
          
          console.log('Required packages:', requiredPackages);
          console.log('Required documents:', requiredDocuments);
          console.log('Required document requests:', requiredDocumentRequests);
          console.log('Required forms:', requiredForms);
          console.log('Submitted package IDs:', Array.from(submittedPackageIds));
          console.log('Submitted document IDs:', Array.from(submittedDocumentIds));
          console.log('Submitted document request IDs:', Array.from(submittedDocumentRequestIds));
          console.log('Approved package IDs:', Array.from(approvedPackageIds));
          console.log('Approved document IDs:', Array.from(approvedDocumentIds));
          console.log('Approved document request IDs:', Array.from(approvedDocumentRequestIds));
          console.log('Total required:', totalRequired);
          console.log('Total submitted:', totalSubmitted);
          console.log('Total approved:', totalApproved);

          // If user has requirements but hasn't submitted all of them, redirect to document completion
          if (totalRequired > 0 && totalSubmitted < totalRequired) {
            console.log('User has incomplete document requirements, redirecting to document completion page');
            setHasRequirements(true);
            setHasChecked(true);
            navigate('/document-completion');
            return;
          } else if (totalRequired > 0 && totalSubmitted === totalRequired) {
            // All documents submitted, check if all are approved
            if (totalApproved === totalRequired) {
              console.log('User has completed all document requirements');
              setIsCompleted(true);
              setHasChecked(true);
            } else {
              console.log('User has submitted all documents but some are pending approval, redirecting to document completion page');
              setHasRequirements(true);
              setHasChecked(true);
              navigate('/document-completion');
              return;
            }
          } else {
            console.log('User has completed all document requirements');
            setIsCompleted(true);
            setHasChecked(true);
          }
        } catch (error) {
          console.error('Error checking assignments/submissions:', error);
          // If we can't check completion status, redirect to document completion page to be safe
          console.log('Could not verify completion status, redirecting to document completion page');
          setHasRequirements(true);
          setHasChecked(true);
          navigate('/document-completion');
          return;
        }
      } else {
        console.log('User has no document requirements - allowing access immediately');
        setIsCompleted(true); // Mark as completed since there's nothing to complete
        setHasChecked(true);
      }
    } catch (error) {
      console.error('Error checking document requirements:', error);
          } finally {
        setIsChecking(false);
        setHasChecked(true);
      }
  };

  console.log('DocumentRequirementCheck render - isChecking:', isChecking, 'isCompleted:', isCompleted, 'hasRequirements:', hasRequirements, 'hasChecked:', hasChecked);
  
  // Don't show spinner if requirements are already completed
  if (isChecking && !isCompleted) {
    console.log('*** SHOWING LOADING SPINNER ***');
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <Spinner animation="border" />
      </div>
    );
  }

  if (hasRequirements) {
    return null; // Will redirect to document completion page
  }

  return children;
};

export default DocumentRequirementCheck;
